import { useTranslation } from "react-i18next";
import { ISystemStatusMessage } from "../@types/systemStatus";
import Icon from "./Icon";
import WorkInProgress from "./WorkInProgress";

interface ISystemStatusBannerProps {
    message: ISystemStatusMessage;
    onDismiss?: () => void;
}

interface ISystemStatusMaintenancePageProps {
    message?: ISystemStatusMessage | null;
}

const severityStyles = {
    info: {
        bar: "bg-[#D2D8E5]",
        iconBg: "bg-gray-50",
        icon: "#3D4A67",
        close: "#4A5C7F",
    },
    warning: {
        bar: "bg-[#FEC84B]",
        iconBg: "bg-[#FEF0C7]",
        icon: "#3D4A67",
        close: "#4A5C7F",
    },
    error: {
        bar: "bg-[#F97066]",
        iconBg: "bg-[#FEE4E2]",
        icon: "#B42318",
        close: "#7A271A",
    },
    critical: {
        bar: "bg-[#F97066]",
        iconBg: "bg-[#FEE4E2]",
        icon: "#B42318",
        close: "#7A271A",
    },
};

const maintenanceFallback = {
    it: {
        title: "Manutenzione in corso",
        description: "Stiamo effettuando un intervento tecnico sul portale.\nCi scusiamo per il disagio e La invitiamo a riprovare piu tardi",
        button: "Consulta il sito",
    },
    en: {
        title: "Maintenance in progress",
        description: "We are performing technical maintenance on the portal.\nWe apologize for the inconvenience and invite you to try again later.",
        button: "Visit the website",
    },
};

function getMessageLocale(language?: string) {
    return language?.toLowerCase().startsWith("it") ? "it" : "en";
}

function getLocalizedText(text: ISystemStatusMessage["title"], language?: string) {
    const locale = getMessageLocale(language);
    return text[locale] ?? text.en ?? text.it ?? "";
}

export function isBlockingSystemStatus(message?: ISystemStatusMessage | null) {
    return message?.severity === "error" || message?.severity === "critical";
}

export function sortSystemStatusMessages(messages: ISystemStatusMessage[]) {
    const severityRank: Record<ISystemStatusMessage["severity"], number> = {
        warning: 0,
        info: 1,
        error: 2,
        critical: 3,
    };

    return [...messages].sort((a, b) => {
        const severityDiff = severityRank[a.severity] - severityRank[b.severity];

        if (severityDiff !== 0) return severityDiff;

        return (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER);
    });
}

export function SystemStatusBanner({ message, onDismiss }: ISystemStatusBannerProps) {
    const { i18n } = useTranslation();
    const styles = severityStyles[message.severity] ?? severityStyles.info;
    const title = getLocalizedText(message.title, i18n.resolvedLanguage ?? i18n.language);
    const description = getLocalizedText(message.description, i18n.resolvedLanguage ?? i18n.language);

    return (
        <div className={`${styles.bar} flex w-full items-center px-4 py-3 md:px-8`} role="status">
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                    <div className={`${styles.iconBg} flex size-[40px] shrink-0 items-center justify-center rounded-full`}>
                        <Icon icon="info" width={20} height={20} stroke={styles.icon} />
                    </div>
                    <div className="flex min-w-0 flex-col gap-2 text-[14px] leading-[20px] md:flex-row md:items-center md:gap-[6px]">
                        <p className="font-semibold text-gray-700">{title}</p>
                        <p className="text-gray-600">{description}</p>
                    </div>
                </div>
                <button
                    type="button"
                    className="flex shrink-0 items-center justify-center rounded-[8px] p-2 transition-colors hover:bg-white/20"
                    aria-label="Chiudi messaggio"
                    onClick={onDismiss}
                >
                    <Icon icon="x-close" stroke={styles.close} />
                </button>
            </div>
        </div>
    );
}

export function SystemStatusMaintenancePage({ message }: ISystemStatusMaintenancePageProps) {
    const { i18n } = useTranslation();
    const language = i18n.resolvedLanguage ?? i18n.language;
    const locale = getMessageLocale(language);
    const title = message ? getLocalizedText(message.title, language) || maintenanceFallback[locale].title : undefined;
    const description = message ? getLocalizedText(message.description, language) || maintenanceFallback[locale].description : undefined;

    return (
        <WorkInProgress
            title={title}
            description={description}
            buttonLabel={maintenanceFallback[locale].button}
        />
    );
}
